import { FC } from "react";
import {
  MDSCheckbox,
  MDSIcon,
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconColor,
  MDSIconKind,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
} from "@/mds";
import type {
  CalendarConnectionConfigurationListItemProps,
  CalendarDisplayStatus,
} from "./calendar-connection-configuration-list-item.types";
import {
  StyledCalendarItemContainer,
  StyledWideLayout,
  StyledNarrowLayout,
  StyledCheckboxWrapper,
  StyledLabelText,
  StyledLabelWithSubtitle,
  StyledStatusContent,
  StyledSubtitleStatus,
  StyledActionsWrapper,
} from "./calendar-connection-configuration-list-item.style";
import { formatCalendarSyncTime } from "./calendar-connection-configuration-list-item.utils";
import { getCalendarSyncStatus } from "@/domains/calendar/getCalendarSyncStatus";

interface StatusContentProps {
  displayStatus: CalendarDisplayStatus | null;
  syncEndedAt: string | null;
}

interface StatusConfig {
  icon: MDSIconKind;
  iconColor: MDSIconColor;
  text: string;
  textColor: MDSTextColor;
}

const getStatusConfig = (
  displayStatus: CalendarDisplayStatus,
  syncEndedAt: string | null
): StatusConfig | null => {
  switch (displayStatus) {
    case "CONNECTING":
      return {
        icon: MDSIconKind.Spinner,
        iconColor: MDSIconColor.Secondary,
        text: "Connecting calendar",
        textColor: MDSTextColor.Secondary,
      };
    case "DISCONNECTING":
      return {
        icon: MDSIconKind.Spinner,
        iconColor: MDSIconColor.Secondary,
        text: "Disconnecting calendar",
        textColor: MDSTextColor.Secondary,
      };
    case "SYNCED": {
      if (!syncEndedAt) return null;
      const formattedTime = formatCalendarSyncTime(syncEndedAt);
      if (!formattedTime) return null;
      return {
        icon: MDSIconKind.Check,
        iconColor: MDSIconColor.Positive,
        text: formattedTime,
        textColor: MDSTextColor.Secondary,
      };
    }
    case "SYNCING":
      return {
        icon: MDSIconKind.Spinner,
        iconColor: MDSIconColor.Secondary,
        text: "Syncing calendar",
        textColor: MDSTextColor.Secondary,
      };
    case "TRYING_TO_CONNECT":
      return {
        icon: MDSIconKind.CloudSlash,
        iconColor: MDSIconColor.Secondary,
        text: "Trying to connect",
        textColor: MDSTextColor.Secondary,
      };
    case "FAILED":
      return {
        icon: MDSIconKind.CloudSlash,
        iconColor: MDSIconColor.Danger,
        text: "Failed to connect",
        textColor: MDSTextColor.Danger,
      };
    default:
      return null;
  }
};

const StatusContent: FC<StatusContentProps> = ({ displayStatus, syncEndedAt }) => {
  if (!displayStatus) return null;
  const config = getStatusConfig(displayStatus, syncEndedAt);
  if (!config) return null;

  return (
    <StyledStatusContent>
      <MDSIcon kind={config.icon} size={10} color={config.iconColor} />
      <MDSText variant={MDSTextVariant.TextXs} color={config.textColor}>
        {config.text}
      </MDSText>
    </StyledStatusContent>
  );
};

const SubtitleStatus: FC<StatusContentProps> = ({ displayStatus, syncEndedAt }) => {
  if (!displayStatus) return null;
  const config = getStatusConfig(displayStatus, syncEndedAt);
  if (!config) return null;

  return (
    <StyledSubtitleStatus>
      <MDSIcon kind={config.icon} size={10} color={config.iconColor} />
      <MDSText variant={MDSTextVariant.TextXs} color={config.textColor}>
        {config.text}
      </MDSText>
    </StyledSubtitleStatus>
  );
};

export const CalendarConnectionConfigurationListItem: FC<
  CalendarConnectionConfigurationListItemProps
> = ({
  id,
  label,
  isCalendarEnabled,
  isDisabled = false,
  togglingStatus = null,
  backgroundColor,
  webhookStatus,
  syncStartedAt,
  syncEndedAt,
  lastSyncEndedWithError,
  isConnected = true,
  onToggle,
  onRetry,
  isRefreshing = false,
  isConnectionDisconnected = false,
}) => {
  // When toggling, show CONNECTING or DISCONNECTING; otherwise show sync status
  const displayStatus = togglingStatus
    ? togglingStatus
    : isCalendarEnabled
      ? getCalendarSyncStatus({
          webhookStatus,
          syncStartedAt,
          syncEndedAt,
          lastSyncEndedWithError,
          isConnected,
        })
      : null;

  const showRefresh = isCalendarEnabled && onRetry;
  const isRefreshDisabled =
    isRefreshing ||
    isConnectionDisconnected ||
    displayStatus === "SYNCING" ||
    displayStatus === "TRYING_TO_CONNECT";

  return (
    <StyledCalendarItemContainer>
      <StyledWideLayout $isLoading={isDisabled}>
        <StyledCheckboxWrapper>
          <MDSCheckbox
            value={id}
            isSelected={isCalendarEnabled}
            isDisabled={isDisabled}
            onChange={onToggle}
            overrideColor={backgroundColor}
          >
            <StyledLabelText variant={MDSTextVariant.TextS}>{label}</StyledLabelText>
          </MDSCheckbox>
        </StyledCheckboxWrapper>
        {displayStatus && <StatusContent displayStatus={displayStatus} syncEndedAt={syncEndedAt} />}
        {showRefresh && (
          <StyledActionsWrapper>
            <MDSIconButton
              iconKind={MDSIconKind.Refresh}
              onPress={onRetry}
              size={MDSIconButtonSize.Medium}
              isDisabled={isRefreshDisabled}
            />
          </StyledActionsWrapper>
        )}
      </StyledWideLayout>

      <StyledNarrowLayout $isLoading={isDisabled}>
        <StyledCheckboxWrapper>
          <MDSCheckbox
            value={id}
            isSelected={isCalendarEnabled}
            isDisabled={isDisabled}
            onChange={onToggle}
            overrideColor={backgroundColor}
          >
            <StyledLabelWithSubtitle>
              <StyledLabelText variant={MDSTextVariant.TextS}>{label}</StyledLabelText>
              {displayStatus && (
                <SubtitleStatus displayStatus={displayStatus} syncEndedAt={syncEndedAt} />
              )}
            </StyledLabelWithSubtitle>
          </MDSCheckbox>
        </StyledCheckboxWrapper>
        {showRefresh && (
          <StyledActionsWrapper>
            <MDSIconButton
              iconKind={MDSIconKind.Refresh}
              onPress={onRetry}
              size={MDSIconButtonSize.Medium}
              isDisabled={isRefreshDisabled}
            />
          </StyledActionsWrapper>
        )}
      </StyledNarrowLayout>
    </StyledCalendarItemContainer>
  );
};
